import { AUDIT_ACTIONS, AUDIT_ENTITY_TYPES, UUID_PATTERN } from '@servicetim/shared';
import type { AuditFilters as ApiAuditFilters } from '@servicetim/ui/api';
import {
  AsyncPicker,
  Button,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  useUserOptionLoader,
} from '@servicetim/ui/components';
import { X } from 'lucide-react';
import { useId, useState } from 'react';
import { fromLocalDateTimeInput, humanize, toLocalDateTimeInput } from '../common/format';

/** Server filters (`useAuditLog`) plus the client-side `action` filter the API does not offer. */
export interface AuditFilterState extends ApiAuditFilters {
  action?: (typeof AUDIT_ACTIONS)[number];
}

const ALL = '__all__';

export function AuditFilters({
  value,
  onChange,
}: {
  value: AuditFilterState;
  onChange: (next: AuditFilterState) => void;
}) {
  const id = useId();
  const loadUsers = useUserOptionLoader();
  const [entityIdDraft, setEntityIdDraft] = useState(value.entityId ?? '');
  const entityIdInvalid = entityIdDraft.trim() !== '' && !UUID_PATTERN.test(entityIdDraft.trim());
  const set = <K extends keyof AuditFilterState>(key: K, next: AuditFilterState[K]) =>
    onChange({ ...value, [key]: next });
  const isEmpty = Object.values(value).every((v) => v === undefined);

  return (
    <div className="grid gap-3 rounded-lg border border-border bg-surface-raised p-3 sm:grid-cols-2 lg:grid-cols-4">
      <div className="space-y-1.5">
        <Label htmlFor={`${id}-entity-type`}>Entity type</Label>
        <Select
          value={value.entityType ?? ALL}
          onValueChange={(v) =>
            set('entityType', v === ALL ? undefined : (v as AuditFilterState['entityType']))
          }
        >
          <SelectTrigger id={`${id}-entity-type`}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>All types</SelectItem>
            {AUDIT_ENTITY_TYPES.map((type) => (
              <SelectItem key={type} value={type}>
                {humanize(type)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div className="space-y-1.5">
        <Label htmlFor={`${id}-action`}>Action</Label>
        <Select
          value={value.action ?? ALL}
          onValueChange={(v) =>
            set('action', v === ALL ? undefined : (v as AuditFilterState['action']))
          }
        >
          <SelectTrigger id={`${id}-action`}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>All actions</SelectItem>
            {AUDIT_ACTIONS.map((action) => (
              <SelectItem key={action} value={action}>
                {action}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div className="space-y-1.5">
        <Label htmlFor={`${id}-actor`}>Actor</Label>
        <AsyncPicker
          id={`${id}-actor`}
          value={value.actorUserId}
          onChange={(userId) => set('actorUserId', userId)}
          load={loadUsers}
          placeholder="Anyone"
        />
      </div>
      <div className="space-y-1.5">
        <Label htmlFor={`${id}-entity-id`}>Entity id</Label>
        <Input
          id={`${id}-entity-id`}
          value={entityIdDraft}
          placeholder="UUID"
          className="font-mono text-xs"
          aria-invalid={entityIdInvalid || undefined}
          aria-describedby={entityIdInvalid ? `${id}-entity-id-error` : undefined}
          onChange={(event) => {
            const next = event.target.value;
            setEntityIdDraft(next);
            const trimmed = next.trim();
            if (trimmed === '') set('entityId', undefined);
            else if (UUID_PATTERN.test(trimmed)) set('entityId', trimmed.toLowerCase());
          }}
        />
        {entityIdInvalid ? (
          <p id={`${id}-entity-id-error`} className="text-amber-700 dark:text-amber-300 text-xs">
            Applied once it is a full UUID.
          </p>
        ) : null}
      </div>
      <div className="space-y-1.5">
        <Label htmlFor={`${id}-since`}>Since</Label>
        <Input
          id={`${id}-since`}
          type="datetime-local"
          value={toLocalDateTimeInput(value.since)}
          onChange={(event) =>
            set('since', fromLocalDateTimeInput(event.target.value) ?? undefined)
          }
        />
      </div>
      <div className="space-y-1.5">
        <Label htmlFor={`${id}-until`}>Until</Label>
        <Input
          id={`${id}-until`}
          type="datetime-local"
          value={toLocalDateTimeInput(value.until)}
          onChange={(event) =>
            set('until', fromLocalDateTimeInput(event.target.value) ?? undefined)
          }
        />
      </div>
      <div className="flex items-end lg:col-span-2 lg:justify-end">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          disabled={isEmpty}
          onClick={() => {
            setEntityIdDraft('');
            onChange({});
          }}
        >
          <X /> Clear filters
        </Button>
      </div>
    </div>
  );
}
